<template>
    <div class="hide-for-group-component">
        <div v-if="isLoading" class="loading-overlay d-flex align-items-center justify-content-center">
            <csp-loading-indicator/>
        </div>
        <csp-hide-for-group-form
            v-if="form"
            :form="form"
            :options="hideForGroupOptions"
            :property="property"
            v-bind="$attrs"
            @change="emitChange"/>

        <csp-hide-for-group-display v-if="!form && hideForGroupOptions && defaultValue !== null"
            :can-toggle-fleet-manager-visibility="canToggleFleetManagerVisibility"
            :mandatory-hide-for-group="mandatoryHideForGroup"
            :options="hideForGroupOptions"
            :toggle-visibility-url="toggleVisibilityUrl"
            :value="defaultValue"
            @fleet-manager-visibility-toggled="emitFleetManagerVisibilityToggled"
            @reload="$emit('reload', $event)"/>
    </div>
</template>

<script>
    import '@scss/components/hide-for-group.scss';
    import Container from '@services/Container';
    import HideForGroupDisplay from '@components/record/detail/HideForGroupDisplay.vue';
    import HideForGroupForm from '@components/record/detail/HideForGroupForm.vue';

    const recordRepository = Container.getInstance().getRecordRepository();

    /**
     * Component for displaying and/or editing HideForGroup.
     * This components includes a formGroupCheckbox, if a form is passed as property
     * Otherwise we render the given data.
     */
    export default {
        name: 'HideForGroup',
        components: {
            'csp-hide-for-group-display': HideForGroupDisplay,
            'csp-hide-for-group-form': HideForGroupForm,
        },
        props: {
            form: {
                type: Object,
                default: null,
            },
            defaultValue: {
                type: Array,
                default: null,
            },
            options: {
                type: Array,
                default: null,
            },
            property: {
                type: String,
                default: null,
            },
            recordId: {
                type: Number,
                required: true,
            },
            toggleVisibilityUrl: {
                type: String,
                default: null,
            },
            canToggleFleetManagerVisibility: {
                type: Boolean,
                default: false,
            },
            damageRecordNoteId: {
                type: Number,
                default: null,
            },
            mandatoryHideForGroup: {
                type: Array,
                default: () => [],
            },
            isLoadingFromParent: {
                type: Boolean,
                default: false,
            },
        },
        emits: ['change', 'reload', 'fleet-manager-visibility-toggled'],
        data() {
            return {
                internalOptions: null,
                isLoadingInternally: 0,
            }
        },
        computed: {
            hideForGroupOptions() {
                const rawOptions = this.internalOptions !== null ? this.internalOptions : this.options;
                if (!rawOptions || this.mandatoryHideForGroup.length === 0) {
                    return rawOptions;
                }
                // Mark options for mandatory-hidden groups as disabled so the checkbox cannot be unchecked.
                return rawOptions.map((option) => ({
                    ...option,
                    disabled: this.mandatoryHideForGroup.includes(option.value),
                }));
            },
            isLoading() {
                return this.isLoadingInternally > 0 || this.isLoadingFromParent;
            },
        },
        mounted() {
            this.isLoadingInternally = 0;
            // Only load available options, if none are set via property
            if (this.options === null) {
                this.loadHideForGroupOptions();
            }
            // Only load default option value, if form is given, but no defaultValue, and no value is set
            if (this.defaultValue === null && this.form && this.form.getProperty(this.property) === null) {
                this.loadHideForGroupDefault();
            }
        },
        methods: {
            emitChange(value) {
                this.$emit('change', value);
            },
            loadHideForGroupOptions() {
                this.isLoadingInternally++;
                recordRepository.hideForGroupOptions(this.recordId).then(
                    (options) => {
                        this.internalOptions = options;
                    }
                ).finally(() => {
                    this.isLoadingInternally--;
                });
            },
            loadHideForGroupDefault() {
                let urlParams;
                if (this.damageRecordNoteId) {
                    urlParams = {
                        damageRecordNoteId: this.damageRecordNoteId,
                    }
                }
                recordRepository.hideForGroupDefault(this.recordId, urlParams).then(
                    (success) => {
                        const hideForGroupDefault = Array.from(success.data.attributes);
                        this.form.setProperty(this.property, hideForGroupDefault);
                    }
                ).finally(() => {
                    this.isLoadingInternally--;
                });
            },
            emitFleetManagerVisibilityToggled() {
                this.$emit('fleet-manager-visibility-toggled');
            },
        },
    }
</script>
